import React from 'react';
import { Check, Upload, Video, X, Youtube } from 'lucide-react';
import { Button } from '../ui/button';

/**
 * Video Approvals tab — combines pending YouTube link submissions with
 * pending file uploads from the Video Library, plus the batch-approve
 * shortcut.
 *
 * Lifted out of AdminPanel.js during the Phase 2 frontend split (Wave 2).
 * Pure presentational — parent owns state and handlers.
 */
export default function YouTubeApprovalTab({
  pendingVideos,
  pendingLibraryVideos,
  handleBatchApproveVideos,
  handleVideoReview,
  handleLibraryVideoReview,
}) {
  const totalPending = pendingVideos.length + pendingLibraryVideos.length;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-playfair font-bold text-foreground flex items-center gap-2">
          <Youtube className="w-6 h-6 text-red-500" /> Video Approvals
          {totalPending > 0 && (
            <span className="bg-red-500 text-white text-sm px-2 py-0.5 rounded-full">
              {totalPending}
            </span>
          )}
        </h2>
        {totalPending > 0 && (
          <Button
            onClick={handleBatchApproveVideos}
            className="bg-green-600 hover:bg-green-700"
            data-testid="batch-approve-videos-btn"
          >
            <Check className="w-4 h-4 mr-2" /> Approve All ({totalPending})
          </Button>
        )}
      </div>
      <p className="text-muted-foreground">
        Review and approve member-submitted videos (YouTube links and uploaded files) before they appear on the platform.
      </p>

      {/* Library Video Uploads */}
      {pendingLibraryVideos.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 mt-6">
            <Upload className="w-5 h-5 text-primary" /> Uploaded Videos ({pendingLibraryVideos.length})
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {pendingLibraryVideos.map(video => (
              <div key={video.id} className="wm-card rounded-lg overflow-hidden" data-testid="pending-library-video-card">
                <div className="aspect-video bg-slate-900 flex items-center justify-center">
                  {video.video_type === 'youtube' ? (
                    <Youtube className="w-16 h-16 text-red-500/50" />
                  ) : (
                    <Video className="w-16 h-16 text-primary/50" />
                  )}
                  <span className="absolute top-2 left-2 bg-primary/90 text-white text-xs px-2 py-1 rounded">
                    {video.video_type === 'youtube' ? 'YouTube' : 'Upload'}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-foreground mb-1">{video.title}</h3>
                  <p className="text-sm text-muted-foreground mb-2">By: {video.user_name} ({video.user_email})</p>
                  {video.file_size > 0 && (
                    <p className="text-xs text-muted-foreground mb-2">
                      Size: {(video.file_size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  )}
                  {video.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{video.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mb-4">
                    Submitted: {new Date(video.created_at).toLocaleString()}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => handleLibraryVideoReview(video.id, true, false)}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-4 h-4 mr-1" /> Approve
                    </Button>
                    <Button
                      onClick={() => handleLibraryVideoReview(video.id, true, true)}
                      className="flex-1 bg-orange-600 hover:bg-orange-700"
                    >
                      <Check className="w-4 h-4 mr-1" /> + Landing
                    </Button>
                    <Button
                      onClick={() => {
                        const reason = prompt('Rejection reason (optional):');
                        handleLibraryVideoReview(video.id, false, false, reason || '');
                      }}
                      variant="destructive"
                      className="flex-1"
                    >
                      <X className="w-4 h-4 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* YouTube Videos */}
      {pendingVideos.length > 0 && (
        <>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-2 mt-6">
            <Youtube className="w-5 h-5 text-red-500" /> YouTube Links ({pendingVideos.length})
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {pendingVideos.map(video => {
              const videoId = video.youtube_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/)?.[1];
              return (
                <div key={video.id} className="wm-card rounded-lg overflow-hidden" data-testid="pending-video-card">
                  <div className="aspect-video bg-slate-900">
                    {videoId ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}`}
                        className="w-full h-full"
                        allowFullScreen
                        title={video.video_title}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Youtube className="w-12 h-12 text-red-500/30" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-foreground mb-1">{video.video_title}</h3>
                    <p className="text-sm text-muted-foreground mb-2">By: {video.user_name} ({video.user_email})</p>
                    {video.description && (
                      <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{video.description}</p>
                    )}
                    <p className="text-xs text-muted-foreground mb-4">
                      Submitted: {new Date(video.created_at).toLocaleString()}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleVideoReview(video.id, true, false)}
                        className="flex-1 bg-green-600 hover:bg-green-700"
                        data-testid="approve-video-btn"
                      >
                        <Check className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button
                        onClick={() => handleVideoReview(video.id, true, true)}
                        className="flex-1 bg-orange-600 hover:bg-orange-700"
                        data-testid="approve-landing-btn"
                      >
                        <Check className="w-4 h-4 mr-1" /> + Landing
                      </Button>
                      <Button
                        onClick={() => {
                          const reason = prompt('Rejection reason (optional):');
                          handleVideoReview(video.id, false, false, reason || '');
                        }}
                        variant="destructive"
                        className="flex-1"
                        data-testid="reject-video-btn"
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {totalPending === 0 && (
        <div className="text-center py-16 wm-card rounded-lg">
          <Youtube className="w-16 h-16 text-red-500/30 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No Pending Videos</h3>
          <p className="text-muted-foreground">All submitted videos have been reviewed.</p>
        </div>
      )}
    </div>
  );
}
